import {
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect } from "react";
import { LoadingButton } from "@mui/lab";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useTranslation } from "react-i18next";
import {
  useAddAccountMutation,
  useAddRecordMutation,
  useUpdateAccountMutation,
  useUpdateRecordMutation,
} from "src/api/tree/tree";

const schema = yup.object().shape({
  name: yup.string().required("Required"),
  balance: yup.number().required("Required"),
});

interface FormInputs extends yup.InferType<typeof schema> {}

interface IModel {
  open: boolean;
  handleClose: () => void;
  dataToEdit?: any;
  refetch: Function;
  parent?: any;
}

const AddEditRecord = (props: IModel) => {
  const { open, handleClose, dataToEdit, refetch, parent } = props;
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const mode = !dataToEdit ? "add" : "edit";
  const { control, handleSubmit, formState, reset, setValue } =
    useForm<FormInputs>({
      mode: "onBlur",
      resolver: yupResolver(schema),
      defaultValues: {},
    });
  const { t } = useTranslation("settings");
  const { t: generalT } = useTranslation("general");
  // APIS
  const { mutate, isPending, status, error } = useAddRecordMutation();
  const {
    mutate: mutateUpdate,
    isPending: isPendingUpdate,
    status: statusUpdate,
    error: errorUpdate,
  } = useUpdateRecordMutation();
  const {
    mutate: mutateAddAccount,
    isPending: isPendingAddAccount,
    status: statusAddAccount,
    error: errorAddAccount,
  } = useAddAccountMutation();
  const {
    mutate: mutateUpdateAccount,
    isPending: isPendingUpdateAccount,
    status: statusUpdateAccount,
    error: errorUpdateAccount,
  } = useUpdateAccountMutation();

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    console.log({ parent });
    const isAccountNode = parent?.node_type === "account";
    const isCategoryNode =
      parent?.node_type === "category" || parent?.node_type === "subcategory";

    if (mode === "add") {
      const recordDataToAdd = {
        chart_account_id: parent?.id,
        name: data.name,
        balance: data.balance,
        show_at_client_invoices: false,
      };
      const accountDataToAdd = {
        chart_account_category_id: parent?.id,
        name: data.name,
        start_balance: data.balance,
        show_at_client_invoices: false,
      };
      if (isCategoryNode) {
        mutateAddAccount(accountDataToAdd);
      } else {
        mutate(recordDataToAdd);
      }
    }

    if (mode === "edit") {
      const baseUpdatePayload = {
        name: data.name,
        show_at_client_invoices: false,
      };
      if (isAccountNode) {
        mutateUpdateAccount({
          id: dataToEdit.id,
          ...baseUpdatePayload,
          start_balance: data.balance,
        });
      } else {
        mutateUpdate({
          id: dataToEdit.id,
          ...baseUpdatePayload,
          balance: data.balance,
        });
      }
    }
  };

  useEffect(() => {
    if (dataToEdit) {
      reset({
        name: dataToEdit.name,
        balance: dataToEdit.total,
      });
    }
  }, [dataToEdit]);

  //Add user success and error handling
  useEffect(() => {
    if (status === "success" || statusAddAccount === "success") {
      handleClose();
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error" || statusAddAccount === "error") {
      const apiError = status === "error" ? error : errorAddAccount;
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            apiError?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status, statusAddAccount]);

  //Update user success and error handling
  useEffect(() => {
    if (statusUpdate === "success" || statusUpdateAccount === "success") {
      handleClose();
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error" || statusUpdateAccount === "error") {
      const apiError =
        statusUpdate === "error" ? errorUpdate : errorUpdateAccount;
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            apiError?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate, statusUpdateAccount]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 537,
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {mode === "add" ? generalT("add") : generalT("edit")}
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  label={t("name")}
                  fullWidth
                  size="small"
                  {...field}
                  error={!!formState.errors.name}
                  helperText={formState.errors.name?.message}
                />
              )}
            />
            <Controller
              name="balance"
              control={control}
              render={({ field }) => (
                <TextField
                  type="number"
                  label={generalT("balance")}
                  fullWidth
                  size="small"
                  {...field}
                  error={!!formState.errors.balance}
                  helperText={formState.errors.balance?.message}
                />
              )}
            />
          </Stack>
          <Stack spacing={2} direction={"row"} mt={3}>
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={handleClose}
            >
              {generalT("cancel")}
            </Button>
            <LoadingButton
              type="submit"
              loading={
                isPending ||
                isPendingUpdate ||
                isPendingAddAccount ||
                isPendingUpdateAccount
              }
              variant="contained"
              fullWidth
            >
              {mode === "add" ? generalT("add") : generalT("save")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default AddEditRecord;
